
import { Exercise } from "../types";
import { WorkoutExercise } from "./WorkoutExercise";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Info } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

interface WorkoutContainerProps {
  exercise: Exercise;
  currentSetIndex: number;
  completedSets: { [key: string]: boolean[] };
  showCompletionCheck: boolean;
  showTimer: boolean;
  isExerciseRest: boolean;
  onSetComplete: (exerciseId: string, setIndex: number) => void;
  onTimerComplete: () => void;
  onTimerSkip: () => void;
  allExercises: Exercise[];
  onExerciseSelect: (exerciseId: string) => void;
  onBack: () => void;
}

export const WorkoutContainer = ({
  exercise,
  currentSetIndex,
  completedSets,
  showCompletionCheck,
  showTimer,
  isExerciseRest,
  onSetComplete,
  onTimerComplete,
  onTimerSkip,
  allExercises,
  onExerciseSelect,
  onBack
}: WorkoutContainerProps) => {
  return (
    <div className="fixed inset-0 dark:bg-black bg-primary/10 ocean:bg-[#081F2B] forest:bg-[#122112] sunset:bg-[#2B1408] flex flex-col">
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-4 left-4 z-[60] bg-white/10 dark:bg-white/5 backdrop-blur-md border border-white/20 hover:bg-white/20 dark:hover:bg-white/10 transition-all duration-300 rounded-xl shadow-lg text-foreground"
        onClick={onBack}
      >
        <ChevronLeft className="h-6 w-6" />
      </Button>

      <div className="relative flex-1 flex flex-col items-center justify-center p-6 pb-safe-area pt-10">
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-2xl font-bold text-black/50 ocean:text-white dark:text-white forest:text-forest sunset:text-sunset">
            {exercise.name}
          </h2>
          <Popover>
            <PopoverTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 rounded-full bg-white/10 dark:bg-white/5 hover:bg-white/20 backdrop-blur-md border border-white/20 text-foreground"
                aria-label="Ver detalle de series"
              >
                <Info className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="center"
              className="w-64 p-0 bg-black/70 backdrop-blur-xl border border-white/10 text-white"
            >
              <div className="p-4 space-y-3">
                <div className="text-sm font-semibold text-center uppercase tracking-wider text-white/80">
                  Detalle del ejercicio
                </div>
                <div className="grid grid-cols-3 gap-2 text-xs uppercase tracking-wider text-white/50 px-2">
                  <span>Serie</span>
                  <span className="text-center">Reps</span>
                  <span className="text-right">Peso (kg)</span>
                </div>
                <div className="space-y-1 max-h-72 overflow-y-auto">
                  {exercise.set_data.map((sd, i) => (
                    <div
                      key={i}
                      className={cn(
                        "grid grid-cols-3 gap-2 px-2 py-2 rounded-lg text-sm",
                        i === currentSetIndex
                          ? "bg-primary/20 text-primary font-semibold"
                          : "bg-white/5 text-white/90"
                      )}
                    >
                      <span>{i + 1}</span>
                      <span className="text-center">{sd.reps}</span>
                      <span className="text-right">{sd.weight}</span>
                    </div>
                  ))}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
        
        <WorkoutExercise
          exercise={exercise}
          currentSetIndex={currentSetIndex}
          completedSets={completedSets}
          showCompletionCheck={showCompletionCheck}
          showTimer={showTimer}
          isExerciseRest={isExerciseRest}
          onSetComplete={onSetComplete}
          onTimerComplete={onTimerComplete}
          onTimerSkip={onTimerSkip}
          allExercises={allExercises}
          onExerciseSelect={onExerciseSelect}
        />
      </div>
    </div>
  );
};
